import Card from '@/global/components/Card'
import Skeleton from '@/global/components/Skeleton'
import { cn } from '@/lib/utils'
import { usePlanosContext } from '@/pages/Planos'
import { PlanoFixo } from '@/service/api'
import { Divider, MenuItem, TextField } from '@mui/material'
import { orange } from '@mui/material/colors'
import { Button } from 'global/components/Button'
import { memo, useEffect, useState } from 'react'

type Props = {
    planos: PlanoFixo[]
    trocar?: boolean
}

export const PlanosCard = memo<Props>(({ planos, trocar }) => {
    const hasPlanos = planos.length > 0

    const [planosMaisCem, planosMenosCem] = [
        planos.filter(p => p.limiteFuncionarios >= 100),
        planos.filter(p => p.limiteFuncionarios < 100),
    ]
    const [planoSelecionado, setPlano] = useState<PlanoFixo>()

    useEffect(() => {
        if (!planoSelecionado && planosMaisCem.at(0)) setPlano(planosMaisCem[0])
    }, [planosMaisCem])

    return (
        <div
            className={cn(
                'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
                'gap-6 p-4',
                'overflow-y-auto max-h-full h-full',
                'justify-items-center',
            )}
        >
            {[
                hasPlanos
                    ? planosMenosCem?.map((plano, index) => {
                          return (
                              <CardPlan
                                  key={index}
                                  trocar={trocar}
                                  limiteFuncionarios={plano.limiteFuncionarios}
                                  periodicity={plano.periodicity}
                                  preco={+plano.price}
                              />
                          )
                      })
                    : Array.from({ length: 3 }).map((_, index) => (
                          <div className="w-[300px]" key={index}>
                              <Card className="rounded-xl h-[430px]">
                                  <Skeleton />
                              </Card>
                          </div>
                      )),
                !!planoSelecionado && (
                    <div className="w-[300px]" key="plano-selecionado">
                        <Card className="rounded-xl h-[430px] flex flex-col items-center justify-evenly">
                            <h6 className="text-lg font-semibold text-center">
                                Precisa de um plano com mais funcionários?
                            </h6>
                            <Divider variant="middle" />
                            <TextField
                                label="Faixa de colaboradores"
                                fullWidth
                                select
                                SelectProps={{
                                    value: planoSelecionado.limiteFuncionarios.toString(),
                                }}
                                onChange={({ target: { value: id } }) => {
                                    setPlano(
                                        planosMaisCem.find(
                                            p =>
                                                p.limiteFuncionarios.toString() ==
                                                id,
                                        )!,
                                    )
                                }}
                            >
                                {planosMaisCem?.map((plano, index) => (
                                    <MenuItem
                                        key={index}
                                        value={plano.limiteFuncionarios.toString()}
                                    >
                                        até {plano.limiteFuncionarios}&nbsp;
                                        funcionários
                                    </MenuItem>
                                ))}
                            </TextField>
                            <DividerDescontoPlano
                                periodicity={planoSelecionado.periodicity}
                                isDiferenciado={
                                    planoSelecionado.limiteFuncionarios == 0
                                }
                            />
                            <PrecoPlano preco={+planoSelecionado.price} />
                            <DividerPeriodoPlano
                                periodicity={planoSelecionado.periodicity}
                            />
                            <BotaoContratarPlano
                                limiteFuncionarios={
                                    planoSelecionado.limiteFuncionarios
                                }
                                periodicity={planoSelecionado.periodicity}
                                trocar={trocar}
                            />
                        </Card>
                    </div>
                ),
            ]}
        </div>
    )
})

function CardPlan({
    trocar,
    limiteFuncionarios,
    periodicity,
    preco,
}: Pick<PlanoFixo, 'limiteFuncionarios' | 'periodicity'> & {
    trocar?: boolean
    preco: number
}) {
    const { PlanoDiferenciado } = usePlanosContext()

    const name = (() => {
        if (limiteFuncionarios == 0 && PlanoDiferenciado) {
            return `até ${PlanoDiferenciado.limiteFuncionarios} funcionários`
        } else if (limiteFuncionarios != 0) {
            return `até ${limiteFuncionarios} funcionários`
        } else {
            return `Plano especial`
        }
    })()

    return (
        <div className="w-[300px]">
            <Card className="rounded-xl min-h-[430px]">
                <div>
                    <div className="flex justify-center items-center py-4 rounded-md text-gray-700">
                        <h6 className="text-lg font-semibold flex justify-center items-center">
                            <strong>{name}</strong>
                        </h6>
                    </div>
                    <DividerDescontoPlano
                        periodicity={periodicity}
                        isDiferenciado={limiteFuncionarios == 0}
                    />
                    <div
                        className={cn(
                            'flex flex-col justify-center items-center',
                            ['yearly', 'quarterly'].includes(periodicity)
                                ? 'my-10'
                                : 'my-[51.6px]',
                        )}
                    >
                        <PrecoPlano preco={preco} />
                    </div>
                    <DividerPeriodoPlano periodicity={periodicity} />
                    <br />
                    <div className="flex justify-center items-center px-4">
                        <BotaoContratarPlano
                            limiteFuncionarios={limiteFuncionarios}
                            periodicity={periodicity}
                            trocar={trocar}
                        />
                    </div>
                    <PeriodoRenovacaoPlano periodicity={periodicity} />
                </div>
            </Card>
        </div>
    )
}

function PrecoPlano({ preco }: { preco: number }) {
    return (
        <div className="flex justify-center items-baseline flex-wrap [overflow-wrap:anywhere] text-gray-700">
            <h4 className="text-xl font-bold -ml-4">R$</h4>
            <h2 className="text-xl font-bold">
                {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                })
                    .format(preco / 100)
                    .split(',')[0]
                    .replace('R$', '')}
            </h2>
            <h6 className="text-xl font-semibold">
                ,{(preco / 100).toFixed(2).split('.')[1]}
            </h6>
        </div>
    )
}

function DividerPeriodoPlano({ periodicity }: Pick<PlanoFixo, 'periodicity'>) {
    const mesAno = periodicity === 'monthly' ? 'mês' : 'ano'

    return (
        <Divider
            variant="middle"
            sx={{
                width: '-webkit-fill-available',
            }}
        >
            <span className="text-base font-medium text-gray-600">
                <strong>POR {mesAno.toUpperCase()}</strong>
            </span>
        </Divider>
    )
}

function PeriodoRenovacaoPlano({
    periodicity,
}: Pick<PlanoFixo, 'periodicity'>) {
    const mesAno =
        periodicity === 'monthly'
            ? 'mês'
            : periodicity === 'quarterly'
              ? 'trimestre'
              : 'ano'

    return (
        <div className="px-4 mt-4">
            <span className="text-sm text-gray-700 tracking-[1.4px] text-center block">
                Este plano é automaticamente renovado a cada {mesAno}
            </span>
        </div>
    )
}

const DividerDescontoPlano = ({
    periodicity,
    isDiferenciado,
}: Pick<PlanoFixo, 'periodicity'> & { isDiferenciado: boolean }) => {
    if (isDiferenciado) return <Divider variant="middle" />
    return (
        <Divider
            variant="middle"
            sx={{
                color: orange[900],
            }}
        >
            {periodicity === 'yearly' && <strong>15% desconto</strong>}
            {periodicity === 'quarterly' && <strong>10% desconto</strong>}
        </Divider>
    )
}

function BotaoContratarPlano({
    limiteFuncionarios,
    periodicity,
    trocar,
}: Pick<PlanoFixo, 'limiteFuncionarios' | 'periodicity'> & {
    trocar?: boolean
}) {
    return (
        <Button
            variant="contained"
            size="large"
            fullWidth
            href={
                trocar
                    ? `/planos/alterar?p=${limiteFuncionarios}-${periodicity}`
                    : `/planos/checkout?p=${limiteFuncionarios}-${periodicity}`
            }
        >
            {trocar ? 'Alterar plano' : 'Contratar plano'}
        </Button>
    )
}
